/**
 * Renders one A2UI surface: walker-resolved tree → React catalog components.
 * Observer: re-renders when the surface (A2uiSurfaceStore) or its data model
 * (UiStateStore, `/surfaces/{surfaceId}`) changes. Contains a local error
 * boundary on purpose — a malformed surface must never crash the stage.
 *
 * Exposes a per-surface `A2uiSurfaceApi`: pointer reads/writes scoped to
 * `/surfaces/{surfaceId}`. Explicit controls stage requests in the composer;
 * conversational dispatch remains available for immediate replies and exploration.
 */
import { Component, useEffect, useRef, useState, type FC, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { useMessagingStore } from '@/app/lib/hooks';
import { wsManager } from '@/app/lib/services/websocket-manager';
import { isSensitiveFieldKind } from '../../../../vendor/agentplace-a2ui/field-sensitivity.ts';
import { isRecord } from '../util/type-guards.ts';
import { resolveSurface, type ResolvedNode } from '../../../../vendor/agentplace-a2ui/walker.ts';
import { evaluateChecks } from '../../../../vendor/agentplace-a2ui/functions.ts';
import {
  collectCheckedNodes,
  readActionEvent,
  resolveActionContext,
} from '../../../../vendor/agentplace-a2ui/actions.ts';
import { A2uiSurfaceContext, type A2uiSurfaceApi } from './surface-context.ts';
import { A2UI_REACT_CATALOG, A2uiFallback } from './catalog.tsx';
// Agent-zone registry: the components THIS agent declares.
import { AGENT_SURFACE_COMPONENTS } from '@/app/agent/surfaces/index.ts';
import { BUILTIN_SURFACE_COMPONENTS } from './builtin-catalog/index.ts';
import { useComposerDraft } from '../stage/ComposerDraftContext.tsx';
import { messages } from '../localization/messages.ts';
import { SurfaceHeadingLevel } from './blocks/heading-context.ts';
import { hasStandaloneChild } from './surface-framing.ts';
import { str } from './props.ts';
import type { A2uiSurface } from '../../../../vendor/agentplace-a2ui/types.ts';
import { EXPLORE_CONTENT_ACTION } from './content-exploration.ts';
import type { ComposerItemInput } from '../stage/composer-draft.ts';
import { surfaceNodeForDisplay } from './display-node.ts';

class SurfaceErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override render(): ReactNode {
    if (this.state.failed) {
      return (
        <div className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
          <FormattedMessage {...messages.viewCouldNotDisplay} />
        </div>
      );
    }
    return this.props.children;
  }
}

const NodeView: FC<{ node: ResolvedNode; leading?: boolean; standalone?: boolean }> = ({
  node,
  leading = true,
  standalone = true,
}) => {
  const displayNode = surfaceNodeForDisplay(node);
  const Renderer =
    AGENT_SURFACE_COMPONENTS[node.component] ??
    BUILTIN_SURFACE_COMPONENTS[node.component] ??
    (node.known ? A2UI_REACT_CATALOG[node.component] : undefined);
  const renderChildren = () => (
    <>
      {node.children.map((child, index) => (
        <NodeView
          key={child.id}
          node={child}
          leading={
            leading && index === 0 && (node.component === 'Column' || node.component === 'Row')
          }
          standalone={hasStandaloneChild(node, standalone)}
        />
      ))}
    </>
  );
  if (!Renderer) {
    return <A2uiFallback node={displayNode} renderChildren={renderChildren} />;
  }
  return (
    <SurfaceHeadingLevel.Provider value={leading ? 1 : 2}>
      <Renderer node={displayNode} renderChildren={renderChildren} standalone={standalone} />
    </SurfaceHeadingLevel.Provider>
  );
};

export const SurfaceRenderer: FC<{
  surfaceId: string;
  snapshot?: A2uiSurface;
  contentId?: string;
  onSubmit?: () => void;
}> = observer(({ surfaceId, snapshot, contentId, onSubmit }) => {
  const composerDraft = useComposerDraft();
  const messagesStore = useMessagingStore();
  const intl = useIntl();
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [submitFailure, setSubmitFailure] = useState<'inputs' | 'unconfirmed' | null>(null);
  const submitting = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const surface = snapshot ?? messagesStore.a2uiSurfaces.surfaces.get(surfaceId);
  const isPreview = () => Boolean(surface?.previewToolCallId);

  const scopedPointer = (pointer: string) => `/surfaces/${surfaceId}${pointer}`;
  const readState = (pointer: string): unknown => messagesStore.uiState.get(pointer);

  const dataModel = (readState(`/surfaces/${surfaceId}`) as Record<string, unknown>) ?? {};
  const tree = surface ? resolveSurface(surface, dataModel) : null;

  /** Immediate conversational dispatch. Reading actions do not submit pending input drafts. */
  const sendAction = async (name: string, context: Record<string, unknown>, label?: string) => {
    if (submitting.current || isPreview()) {
      return;
    }
    submitting.current = true;
    setSubmitFailure(null);
    let inputsSaved = false;
    try {
      if (name !== EXPLORE_CONTENT_ACTION) {
        const submitted = structuredClone(toJS(messagesStore.uiState.state));
        await messagesStore.uiState.syncSubmitted(submitted, (value) =>
          wsManager.stateUpdate(value),
        );
      }
      inputsSaved = true;
      onSubmit?.();
      await messagesStore.sendMessage({
        instruction: str(label).trim() || intl.formatMessage(messages.submit),
        presentationTarget: { surfaceId, ...(contentId ? { contentId } : {}) },
        metadata: { a2uiAction: { surfaceId, name, context } },
      });
    } catch (error) {
      console.error('[SurfaceRenderer] action submission failed', { surfaceId, error });
      if (mounted.current) {
        setSubmitFailure(inputsSaved ? 'unconfirmed' : 'inputs');
      }
    } finally {
      submitting.current = false;
    }
  };

  /** Field ids whose values must never leave the browser, read from the
   *  rendered tree's own `kind` declarations. */
  const sensitiveFieldIds = collectSensitiveFieldIds(tree);

  /** Blur-time sync: the agent sees the form in progress, minus anything
   *  sensitive. Deliberately does not `markSynced()` — the server's copy is
   *  partial, so an incoming snapshot must still lose to what is on screen. */
  const commitTypedValues = async (): Promise<void> => {
    if (isPreview() || sensitiveFieldIds === null) {
      return;
    }
    try {
      const doc = messagesStore.uiState.syncableState((pointer) =>
        sensitiveFieldIds.some((id) => pointer.endsWith(`/${id}`)),
      );
      await wsManager.stateUpdate(toJS(doc));
    } catch (error) {
      console.error('[SurfaceRenderer] blur stateUpdate failed', error);
    }
  };

  const submit = (node: ResolvedNode): void => {
    if (!tree || isPreview() || !composerDraft) {
      return;
    }
    const checkedNodes: ResolvedNode[] = [];
    collectCheckedNodes(tree, checkedNodes);

    const nextErrors: Record<string, string[]> = {};
    let anyFailed = false;
    const surfaceDataModel = readState(`/surfaces/${surfaceId}`) ?? {};
    for (const checkedNode of checkedNodes) {
      const result = evaluateChecks(checkedNode.props.checks, surfaceDataModel);
      if (!result.ok) {
        nextErrors[checkedNode.id] = result.messages;
        anyFailed = true;
      }
    }
    setErrors(nextErrors);
    if (anyFailed) {
      return;
    }

    const actionEvent = readActionEvent(node.props.action);
    if (!actionEvent) {
      return;
    }
    const resolvedContext = resolveActionContext(actionEvent.context, (pointer) =>
      readState(scopedPointer(pointer)),
    );
    const label = str(node.props.label);
    composerDraft.add({
      id: node.id,
      surfaceId,
      ...(contentId ? { contentId } : {}),
      label,
      instruction: label,
      context: {
        ...resolvedContext,
        component: node.component,
        nodeId: node.id,
        action: actionEvent.name,
      },
    });
  };

  const api: A2uiSurfaceApi = {
    surfaceId,
    contentId,
    ...(composerDraft
      ? {
          addToMessage: (item: ComposerItemInput) => {
            if (!isPreview()) {
              composerDraft.add({ ...item, surfaceId, ...(contentId ? { contentId } : {}) });
            }
          },
        }
      : {}),
    sendIntent: (instruction) => {
      if (isPreview()) {
        return;
      }
      onSubmit?.();
      void messagesStore.sendMessage({
        instruction,
        presentationTarget: { surfaceId, ...(contentId ? { contentId } : {}) },
      });
    },
    getValue: (pointer) => readState(scopedPointer(pointer)),
    setValue: (pointer, value) => {
      if (!isPreview()) {
        messagesStore.uiState.setLocal(scopedPointer(pointer), value);
      }
    },
    submit: (node) => {
      void submit(node);
    },
    dispatch: (name, context, label) => {
      const visibleLabel =
        name === EXPLORE_CONTENT_ACTION ? intl.formatMessage(messages.tellMeMore) : label;
      void sendAction(name, context, visibleLabel);
    },
    commitValue: () => {
      void commitTypedValues();
    },
    errorsFor: (nodeId) => errors[nodeId] ?? [],
  };

  if (!surface || !tree) {
    return null; // no root yet — progressive stream still arriving
  }
  return (
    <SurfaceErrorBoundary>
      <A2uiSurfaceContext.Provider value={api}>
        <div
          className="stage-surface"
          ref={(element) => {
            if (element) element.inert = isPreview();
          }}
          aria-busy={isPreview()}
          data-surface-preview={isPreview() || undefined}
        >
          <NodeView node={tree} />
        </div>
        {submitFailure ? (
          <p role="alert" className="mt-3 text-sm text-destructive">
            <FormattedMessage
              {...(submitFailure === 'inputs'
                ? messages.surfaceSubmitFailed
                : messages.surfaceSubmissionUnconfirmed)}
            />
          </p>
        ) : null}
      </A2uiSurfaceContext.Provider>
    </SurfaceErrorBoundary>
  );
});

/** Walk the resolved tree for field declarations and return the ids whose
 *  `kind` is withheld from state sync. Returns `null` when there is no tree. */
function collectSensitiveFieldIds(tree: ResolvedNode | null): string[] | null {
  if (!tree) {
    return null;
  }
  const ids: string[] = [];
  const visit = (node: ResolvedNode): void => {
    const fields = node.props.fields;
    if (Array.isArray(fields)) {
      for (const field of fields) {
        if (isRecord(field) && typeof field.id === 'string' && isSensitiveFieldKind(field.kind)) {
          ids.push(field.id);
        }
      }
    }
    for (const child of node.children) {
      visit(child);
    }
  };
  visit(tree);
  return ids;
}
